'use client';

import { c as _c } from "react/compiler-runtime";
import { keepPreviousData } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { ArticleThemesEnum, ArticleTopicsEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import type { ArticleTypesEnum } from '@sola/contentful-model/lib/taxonomy-values.ts';
import { useListArticlesQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { SearchResultProps, SearchResultsInput } from '@sola/skippy-search-sdk/lib/schema.ts';
import { ArticleSortByEnum, useArticleQueryParams, usePaginationQueryParams } from '../../utils/query-params.tsx';
export type UseArticleListOptions = {
  articleType?: string;
  country?: string;
  theme?: string;
  topic?: string;
  limit?: number;
  variant?: 'default' | 'related';
  excludeContentfulEntryID?: string;
  hidePagination?: boolean;
  /** When true, filters are read from URL params (dynamic). When false, uses static CMS props. */
  isDynamic?: boolean;
  onArticleLoad?: (info: {
    totalCount: number;
  }) => void;
};
const sortByValues = {
  featured: ArticleSortByEnum.enum.promotedContent,
  ascending: ArticleSortByEnum.enum.asc,
  descending: ArticleSortByEnum.enum.desc
} as const;

/** Derive column counts from limit and variant. */
export const buildColumnLayout = (limit: number, variant: 'default' | 'related') => ({
  columns: 1,
  columnsMd: Math.min(limit, 3),
  columnsLg: variant === 'related' ? Math.min(limit, 3) : Math.min(limit, 4)
});

/** Build the search query config from resolved filter values. */
export const buildArticleQueryConfig = ({
  articleType,
  topic,
  theme,
  country,
  excludeContentfulEntryID,
  sortBy,
  variant
}: {
  articleType?: string;
  topic?: string | null;
  theme?: string | null;
  country?: string | null;
  excludeContentfulEntryID?: string;
  sortBy: string;
  variant: 'default' | 'related';
}): {
  queryConfig: Partial<SearchResultsInput>;
  mustNot: Record<string, string>;
} => {
  const sortDir: 'asc' | 'desc' = sortBy === sortByValues.ascending ? 'asc' : 'desc';
  const isFeaturedSort = sortBy === sortByValues.featured;
  const filterString = {
    'taxonomy.articleType.keyword': articleType || undefined,
    'taxonomy.articleTopics.keyword': topic || undefined,
    'taxonomy.articleThemes.keyword': theme || undefined,
    'taxonomy.programCountry.keyword': country || undefined
  };
  const mustNot: Record<string, string> = excludeContentfulEntryID ? {
    'contentfulEntryID.keyword': excludeContentfulEntryID
  } : {};
  const baseConfig = isFeaturedSort ? {
    should: JSON.stringify({
      'taxonomy.other.keyword': 'promotedContent'
    }),
    shouldStrictness: 0,
    sort: 'datePublished' as const,
    sortDir: 'desc' as const
  } : {
    sort: 'datePublished' as const,
    sortDir
  };
  const queryConfig = variant === 'related' ? {
    ...baseConfig,
    should: JSON.stringify(filterString),
    shouldStrictness: 0
  } : {
    ...baseConfig,
    filters: JSON.stringify(filterString)
  };
  return {
    queryConfig,
    mustNot
  };
};
export const useArticleList = t0 => {
  const $ = _c(43);
  const {
    articleType,
    country,
    theme,
    topic,
    limit,
    variant: t1,
    excludeContentfulEntryID,
    hidePagination: t2,
    isDynamic: t3,
    onArticleLoad
  } = t0;
  const variant = t1 === undefined ? "default" : t1;
  const hidePagination = t2 === undefined ? false : t2;
  const isDynamic = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== limit) {
    t4 = {
      perPage: limit
    };
    $[0] = limit;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    articleFilters
  } = useArticleQueryParams(t4);
  const {
    currentPage
  } = usePaginationQueryParams();
  const sortBy = isDynamic ? articleFilters.sortby || "desc" : "desc";
  const resolvedLimit = isDynamic ? !hidePagination && articleFilters.limit || limit || 12 : limit || 12;
  const resolvedTopic = topic || (isDynamic ? articleFilters.topic : null);
  const resolvedTheme = theme || (isDynamic ? articleFilters.theme : null);
  const resolvedCountry = country || (isDynamic ? articleFilters.country : null);
  let t5;
  if ($[2] !== resolvedLimit || $[3] !== variant) {
    t5 = buildColumnLayout(resolvedLimit, variant);
    $[2] = resolvedLimit;
    $[3] = variant;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const columnLayout = t5;
  let t10;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== articleType || $[6] !== currentPage || $[7] !== excludeContentfulEntryID || $[8] !== isDynamic || $[9] !== resolvedCountry || $[10] !== resolvedLimit || $[11] !== resolvedTheme || $[12] !== resolvedTopic || $[13] !== sortBy || $[14] !== variant) {
    const {
      queryConfig,
      mustNot
    } = buildArticleQueryConfig({
      articleType,
      topic: resolvedTopic,
      theme: resolvedTheme,
      country: resolvedCountry,
      excludeContentfulEntryID,
      sortBy,
      variant
    });
    t10 = useListArticlesQuery;
    t6 = queryConfig;
    t7 = resolvedLimit;
    t8 = isDynamic ? currentPage * resolvedLimit : 0;
    t9 = JSON.stringify(mustNot);
    $[5] = articleType;
    $[6] = currentPage;
    $[7] = excludeContentfulEntryID;
    $[8] = isDynamic;
    $[9] = resolvedCountry;
    $[10] = resolvedLimit;
    $[11] = resolvedTheme;
    $[12] = resolvedTopic;
    $[13] = sortBy;
    $[14] = variant;
    $[15] = t10;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t10 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t11;
  if ($[20] !== t6 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t11 = {
      ...t6,
      perPage: t7,
      offset: t8,
      must_not: t9
    };
    $[20] = t6;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      placeholderData: keepPreviousData
    };
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const articleQuery = t10(t11, t12);
  const {
    data: articleData,
    isLoading,
    isError
  } = articleQuery;
  const articles = articleData?.items;
  const totalCount = articleData?.totalCount || 0;
  const showLoading = isLoading && !articleData;
  const shouldShowPagination = isDynamic && variant === "default" && !hidePagination;
  let t13;
  let t14;
  if ($[26] !== isLoading || $[27] !== onArticleLoad || $[28] !== totalCount) {
    t13 = () => {
      if (onArticleLoad && !isLoading) {
        onArticleLoad({
          totalCount
        });
      }
    };
    t14 = [totalCount, isLoading, onArticleLoad];
    $[26] = isLoading;
    $[27] = onArticleLoad;
    $[28] = totalCount;
    $[29] = t13;
    $[30] = t14;
  } else {
    t13 = $[29];
    t14 = $[30];
  }
  useEffect(t13, t14);
  let t15;
  if ($[31] !== articles) {
    t15 = articles?.filter(_temp);
    $[31] = articles;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  const uniqueArticles = t15;
  let t16;
  if ($[33] !== articleData || $[34] !== articleType || $[35] !== columnLayout || $[36] !== isError || $[37] !== resolvedLimit || $[38] !== shouldShowPagination || $[39] !== showLoading || $[40] !== totalCount || $[41] !== uniqueArticles) {
    t16 = {
      articles: uniqueArticles,
      articleData,
      isLoading: showLoading,
      isError,
      totalCount,
      resolvedLimit,
      columnLayout,
      shouldShowPagination,
      articleType
    };
    $[33] = articleData;
    $[34] = articleType;
    $[35] = columnLayout;
    $[36] = isError;
    $[37] = resolvedLimit;
    $[38] = shouldShowPagination;
    $[39] = showLoading;
    $[40] = totalCount;
    $[41] = uniqueArticles;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  return t16;
};
export const getEyebrow = (result: SearchResultProps, articleType: string | undefined, t: (key: string) => string): string | undefined => {
  if (!articleType && result.taxonomyArticleType) {
    return t(`articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`);
  }
  if (result.taxonomyTopic) {
    return t(`articles:taxonomy.articleTopics.${result.taxonomyTopic[0] as ArticleTopicsEnum}.name`);
  }
  if (result.taxonomyArticleTheme) {
    return t(`articles:taxonomy.articleThemes.${result.taxonomyArticleTheme[0] as ArticleThemesEnum}.name`);
  }
  if (result.taxonomyArticleType) {
    return t(`articles:taxonomy.articleTypes.${result.taxonomyArticleType as ArticleTypesEnum}.singularName`);
  }
  return undefined;
};
function _temp(a, i, arr) {
  return arr.findIndex(b => b.contentfulEntryID === a.contentfulEntryID) === i;
}